import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Truck, RefreshCw, CreditCard, Headphones, Plus, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { mediaUrl, asArray } from '../lib/api';
import Reveal from '../components/Reveal';
import Marquee from '../components/Marquee';
import BentoGrid from '../components/BentoGrid';
import { useQuoteCart } from '../context/QuoteCartContext';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';
import { WHY_US, APPROVALS, HERO_IMAGES } from '../mock';

const WHY_ICONS = [Truck, RefreshCw, CreditCard, Headphones];
const PAGE = 12;

export function PageHero({ title, crumb, image }) {
  return (
    <section className="relative h-[360px] flex items-end overflow-hidden noise">
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#070a12] via-[#070a12]/70 to-[#070a12]/40" />
      <div className="orb w-[380px] h-[380px] bg-primary/20 top-10 right-0 animate-glow" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative max-w-7xl mx-auto px-6 w-full pb-14">
        <h1 className="font-heading font-extrabold text-5xl md:text-6xl text-white">{title}</h1>
        <div className="flex items-center gap-2 text-gray-400 mt-3 font-heading"><Link to="/" className="hover:text-primary">Home</Link><span>/</span><span className="fire-text">{crumb}</span></div>
      </motion.div>
    </section>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  const [count, setCount] = useState(PAGE);
  const [certs, setCerts] = useState([]);
  const { addItem } = useQuoteCart();
  const { toast } = useToast();
  const location = useLocation();

  useEffect(() => {
    api.get('/products').then((res) => setProducts(asArray(res.data))).catch(() => {});
    api.get('/certifications').then((res) => setCerts(asArray(res.data))).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get('cat');
    if (c) setCat(c);
  }, [location.search]);

  const categories = ['All', ...products.reduce((acc, p) => (acc.includes(p.category) ? acc : [...acc, p.category]), [])];
  const byCat = cat === 'All' ? products : products.filter((p) => p.category === cat);
  const all = q.trim() ? byCat.filter((p) => `${p.name} ${p.category} ${p.desc || ''}`.toLowerCase().includes(q.trim().toLowerCase())) : byCat;
  const list = all.slice(0, count);

  const quickAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    const added = addItem({ productId: p.id, name: p.name, image: p.image, sku: p.variants?.[0]?.label || '' }, false);
    toast({ title: added ? 'Added to enquiry' : 'Already in your enquiry list', description: added ? `${p.name} added. Open the cart to send your enquiry.` : `${p.name} is already in your list.` });
  };

  return (
    <div className="bg-[#070a12]">
      <PageHero title="Products" crumb="Products" image={HERO_IMAGES[2]} />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-xl mx-auto mb-8 relative">
            <Search className="w-5 h-5 text-gray-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <Input data-testid="product-search" value={q} onChange={(e) => { setQ(e.target.value); setCount(PAGE); }} placeholder="Search fire extinguishers, hydrant valves, alarms…" className="h-13 pl-11 pr-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-2xl" style={{ height: 52 }} />
            {q && <button onClick={() => setQ('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white" data-testid="product-search-clear"><X className="w-4 h-4" /></button>}
          </div>
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            {categories.map((c) => (
              <button key={c} data-testid={`product-filter-${c.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => { setCat(c); setCount(PAGE); }}
                className={`px-6 py-2.5 rounded-full font-heading font-semibold text-sm transition-all ${cat === c ? 'bg-gradient-to-r from-primary to-accent text-white shadow-lg shadow-primary/30' : 'glass text-gray-300 hover:text-white'}`}>{c}</button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
            {list.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 80}>
                <Link to={`/product/${p.id}`} data-testid={`product-card-${p.id}`} className="group glass glass-hover rounded-3xl overflow-hidden h-full flex flex-col">
                  <div className="img-zoom h-56 relative">
                    <img src={mediaUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f1a] via-transparent to-transparent" />
                    <button onClick={(e) => quickAdd(e, p)} data-testid={`product-quickadd-${p.id}`} title="Add to enquiry" className="absolute top-3 right-3 grid place-items-center w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent text-white shadow-lg shadow-primary/30 opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105">
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-xs font-semibold fire-text uppercase tracking-wider">{p.category}</span>
                    <h3 className="font-heading font-bold text-lg text-white mt-1 group-hover:fire-text transition-all">{p.name}</h3>
                    <p className="text-sm text-gray-400 mt-2 flex-1">{p.desc}</p>
                    <div className="flex items-center justify-between mt-5">
                      <span className="inline-flex items-center gap-1.5 text-primary font-semibold text-sm">View details <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                      <span onClick={(e) => quickAdd(e, p)} className="inline-flex items-center gap-1 text-xs font-heading font-semibold text-gray-300 hover:text-white border border-white/15 rounded-lg px-2.5 py-1.5 transition-colors"><Plus className="w-3.5 h-3.5" /> Enquire</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {list.length === 0 && (
            <p className="text-center text-gray-400 py-16" data-testid="products-empty">No products match "{q}". Try another search.</p>
          )}

          {count < all.length && (
            <div className="text-center mt-12">
              <Button onClick={() => setCount((c) => c + PAGE)} variant="outline" size="lg" className="bg-white/5 border-white/15 text-white hover:bg-white/10">Load More</Button>
            </div>
          )}
        </div>
      </section>

      <BentoGrid />

      <section className="py-16 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 mb-8"><p className="text-center section-eyebrow text-gray-500 justify-center">Approvals & Certifications</p></div>
        <Marquee items={(certs.length ? certs : APPROVALS.map((a) => ({ name: a })))} renderItem={(c) => (
          c.logo
            ? <img src={mediaUrl(c.logo)} alt={c.name} className="h-20 w-auto object-contain mx-8 opacity-80 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0 hover:scale-110 hover:drop-shadow-[0_0_16px_rgba(232,51,42,0.5)]" />
            : <span className="font-display font-bold text-2xl text-white/90 whitespace-nowrap px-4">{c.name}</span>
        )} />
      </section>

      <section className="py-20 bg-[#05070d]">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_US.map((w, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <Reveal key={w.title} delay={i * 90} className="text-center">
                <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent text-white mb-4"><Icon className="w-7 h-7" /></span>
                <h3 className="font-heading font-bold text-lg text-white">{w.title}</h3>
                <p className="text-sm text-gray-400 mt-2">{w.desc}</p>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
